import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addPaymentMethodThunk,
  deletePaymentMethodThunk,
  fetchPaymentMethodDetailsThunk,
  fetchPaymentMethodsThunk,
  selectBillingState,
} from "../store/billingSlice";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { isAdmin } from "../../../shared/utils/roles";

export const usePaymentMethods = () => {
  const dispatch = useDispatch();
  const state = useSelector(selectBillingState);
  const currentUser = useSelector(selectCurrentUser);
  const canEditPaymentMethods = isAdmin(currentUser?.user_role);

  useEffect(() => {
    if (state.hasPaymentMethodsLoaded) return;
    dispatch(fetchPaymentMethodsThunk());
  }, [dispatch, state.hasPaymentMethodsLoaded]);

  const handleAdd = useCallback(
    async (payload) => {
      if (!canEditPaymentMethods) {
        return { error: { message: "Payment methods are read-only for this role." } };
      }

      const action = await dispatch(addPaymentMethodThunk({ payload }));
      if (addPaymentMethodThunk.fulfilled.match(action)) {
        return { success: true };
      }

      return {
        error: {
          message: action.payload || "Unable to add payment method.",
        },
      };
    },
    [canEditPaymentMethods, dispatch],
  );

  const loadDetails = useCallback(
    async (paymentMethodId) => {
      const action = await dispatch(fetchPaymentMethodDetailsThunk(paymentMethodId));
      return fetchPaymentMethodDetailsThunk.fulfilled.match(action);
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    async (paymentMethodId) => {
      if (!canEditPaymentMethods) {
        return false;
      }

      const action = await dispatch(deletePaymentMethodThunk(paymentMethodId));
      return deletePaymentMethodThunk.fulfilled.match(action);
    },
    [canEditPaymentMethods, dispatch],
  );

  return {
    cards: state.cards,
    selectedMethodDetails: state.selectedMethodDetails,
    isLoading: state.isLoadingPaymentMethods,
    isSubmitting: state.isSubmittingPaymentMethod,
    isDeleting: state.isDeletingPaymentMethod,
    isLoadingDetails: state.isLoadingDetails,
    apiError: state.paymentMethodsError,
    detailsError: state.paymentMethodDetailsError,
    canEditPaymentMethods,
    handleAdd,
    loadDetails,
    handleDelete,
  };
};

export default usePaymentMethods;
